/**
 * LicenseSettings — License status, plan tier, and remaining minutes.
 */

import { useState } from "react";
import { SettingsGroup, SettingsRow } from "./SettingsPrimitives";
import { useCloudCredits } from "@/hooks/useCloudCredits";
import { TopUpCreditsModal } from "@/components/checkout/TopUpCreditsModal";
import { getTransport } from "@/lib/transport";

export function LicenseSettings() {
  const { credits, minutesRemaining, status, licenseKey: activeKey, email } = useCloudCredits();
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);

  const licenseKey = activeKey || "SP-LITE-ACTIVE";
  const maskedKey = licenseKey.length > 8
    ? `${licenseKey.slice(0, 7)}••••••••••••${licenseKey.slice(-4)}`
    : licenseKey;

  const isDepleted = status === "depleted";
  const isLow = status === "low";

  return (
    <div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)", marginBottom: "var(--space-6)" }}>
        License &amp; Account
      </div>

      <SettingsGroup title="Streaming Compute Quota &amp; Balance">
        <div style={{
          padding: "var(--space-4)",
          background: "var(--bg-overlay)",
          borderRadius: "var(--radius-lg)",
          border: `1px solid ${isDepleted ? "rgba(239, 68, 68, 0.4)" : isLow ? "rgba(245, 158, 11, 0.4)" : "var(--border-subtle)"}`,
          marginBottom: "var(--space-3)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "12px" }}>
            <div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
                Available Cloud Compute Time
              </div>
              <div style={{ fontSize: "24px", fontWeight: "var(--weight-bold)", color: isDepleted ? "var(--status-error)" : isLow ? "var(--status-warn)" : "var(--accent)", marginTop: "2px" }}>
                {minutesRemaining !== null ? `${minutesRemaining} mins` : "Checking..."}
                <span style={{ fontSize: "15px", fontWeight: 500, color: "var(--text-secondary)", marginLeft: "8px" }}>
                  (${credits !== null ? credits.toFixed(2) : "0.00"} USD)
                </span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                color: isDepleted ? "var(--status-error)" : isLow ? "var(--status-warn)" : "var(--status-live)",
                background: isDepleted ? "var(--status-error-dim)" : isLow ? "rgba(245, 158, 11, 0.15)" : "var(--status-live-dim)",
                padding: "3px 10px",
                borderRadius: "var(--radius-sm)",
              }}>
                {isDepleted ? "Depleted · Top Up Required" : isLow ? "Low Balance" : "Active · Operational"}
              </span>
            </div>
          </div>

          <button
            onClick={() => setIsTopUpOpen(true)}
            style={{
              width: "100%",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              background: isDepleted ? "var(--status-error)" : "var(--accent)",
              color: "#000",
              fontWeight: 700,
              fontSize: "var(--text-xs)",
              borderRadius: "var(--radius-md)",
              border: "none",
              cursor: "pointer",
              transition: "all var(--duration-fast)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Add Cloud Compute Minutes</span>
          </button>
        </div>
      </SettingsGroup>

      <SettingsGroup title="Authoritative LiveKit Metering Rates">
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
        }}>
          <div style={{
            padding: "12px",
            background: "var(--bg-surface)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-subtle)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" }}>Hourly Rate</div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)", marginTop: "4px" }}>$6.58 <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 400 }}>/ hr</span></div>
          </div>

          <div style={{
            padding: "12px",
            background: "var(--bg-surface)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-subtle)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" }}>Minute Rate</div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--accent)", marginTop: "4px" }}>~$0.11 <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 400 }}>(10.97¢)</span></div>
          </div>

          <div style={{
            padding: "12px",
            background: "var(--bg-surface)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-subtle)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" }}>Second Rate</div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)", marginTop: "4px" }}>$0.0018 <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 400 }}>/ sec</span></div>
          </div>
        </div>
        <div style={{ fontSize: "11px", color: "var(--text-muted)", lineHeight: 1.5, padding: "0 4px" }}>
          Streaming compute is metered second-by-second via official LiveKit Cloud Webhooks with zero round-up fees.
        </div>
      </SettingsGroup>

      <SettingsGroup title="Subscription &amp; Authentication">
        <SettingsRow label="Plan Tier" description="Dedicated cloud node allocation">
          <span style={{
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-bold)",
            color: "var(--status-live)",
            background: "var(--status-live-dim)",
            padding: "2px 8px",
            borderRadius: "var(--radius-sm)",
          }}>
            StreamPersona Lite (Cloud Node)
          </span>
        </SettingsRow>

        <SettingsRow label="License Key" description="Cryptographic Ed25519 authenticated token">
          <span style={{ fontSize: "var(--text-xs)", fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>
            {maskedKey}
          </span>
        </SettingsRow>

        <SettingsRow label="Cloud GPU Allocation" description="Dedicated RTX 5090 Blackwell node">
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", fontWeight: 500 }}>
            Active · Multi-Tenant Isolated
          </span>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Support &amp; Portal">
        <div style={{
          padding: "var(--space-4)",
          background: "var(--bg-overlay)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-subtle)",
          fontSize: "var(--text-xs)",
          color: "var(--text-secondary)",
          lineHeight: 1.6,
        }}>
          <div>Need to add more streaming minutes or request Studio Workstation access?</div>
          <div style={{ marginTop: "6px" }}>
            Contact: <a href="mailto:support@streampersona.app" style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>support@streampersona.app</a>
          </div>
          <div style={{ marginTop: "4px" }}>
            Portal: <a href="https://streampersona.app" target="_blank" rel="noreferrer" style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>streampersona.app</a>
          </div>
        </div>
      </SettingsGroup>

      <TopUpCreditsModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        transport={getTransport()}
      />
    </div>
  );
}
